import React, { FunctionComponent, KeyboardEvent, useEffect } from "react";

import { AnimatedMount, AnimationType, DelayedUnmount, Layout } from "@multiweb/reactComponents";
import { ANIMATION_DURATION_MS, createFocusWithinObserver, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { MENU_WIDTH } from "../../constants";
import { FloatMenuPanel } from "../FloatMenuPanel";
import { Menu } from "../Menu";
import { MenuPanel } from "../MenuPanel";
import { SubmenuList } from "../SubmenuList";

export type MainMenuComponentProps = {
    /** Příznak, zda je menu viditelné - ať už překrývající či nikoli. */
    isMenuVisible?: boolean;
    /** Příznak, zda je otevřený seznam podmenu. */
    isSubmenuListExpanded?: boolean;
    /** Funkce pro načtení aliasů povolených kořenových uzlů hlavního navigačního menu. */
    loadAllowedMenuItemCodes: () => void;
    /** Funkce pro nastavení počátečního stavu pro rozbalení/sbalení menu. */
    setInitialExpandedState: () => void;
    /** Funkce pro nastavení příznaku pro zobrazovaní popisků v menu panelu. */
    setInitialMenuPanelCaptionsVisibility: () => void;
    /** Funkce pro zpracování události stisku klávesy. */
    onKeyPressed: (event: KeyboardEvent) => void;
    /** Funkce volaná, když je komponenta (nebo některý její potomek) zaměřena. Nevolá se pokud se zaměření pohybuje mezi potomky. */
    onFocusHandler?: () => void;
    /** Funkce volaná, když komponenta (nebo některý její potomek) ztratí zaměření. Nevolá se pokud se zaměření pohybuje mezi potomky. */
    onBlurHandler?: (documentHidden: boolean) => void;
    /** Příznak, zda je menu překrývající. */
    isMenuFloated?: boolean;
    /** Příznak, zda je aktivní float přechod menu. */
    menuFloatTransitionActive: boolean;
};

/** Komponenta reprezentuje celé hlavní menu. Skládá se ze tří částí – panelu menu, menu a podmenu. */
export const MainMenuComponent: FunctionComponent<MainMenuComponentProps> = ({
    isMenuVisible = false,
    isSubmenuListExpanded = false,
    loadAllowedMenuItemCodes,
    setInitialExpandedState,
    setInitialMenuPanelCaptionsVisibility,
    onKeyPressed,
    onFocusHandler,
    onBlurHandler,
    isMenuFloated = false,
    menuFloatTransitionActive,
}) => {
    useEffect(() => {
        loadAllowedMenuItemCodes();
        setInitialExpandedState();
        setInitialMenuPanelCaptionsVisibility();
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { onFocus, onBlur } = createFocusWithinObserver(onFocusHandler, onBlurHandler);

    const menuContent = (
        <AnimatedMount
            mounted={isMenuVisible}
            animationDurationMs={ANIMATION_DURATION_MS}
            width={MENU_WIDTH}
            animationType={AnimationType.width}
        >
            <Menu />
        </AnimatedMount>
    );

    return (
        <div
            className="main-menu-component"
            tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE}
            onKeyDown={onKeyPressed}
            {...{ onFocus, onBlur }}
        >
            <Layout>
                <MenuPanel />
                <FloatMenuPanel disabled={!(isMenuFloated || menuFloatTransitionActive)}>
                    {menuContent}
                    <DelayedUnmount
                        delayMs={ANIMATION_DURATION_MS}
                        mounted={isSubmenuListExpanded}
                    >
                        <FloatMenuPanel disabled={isMenuFloated}>
                            <SubmenuList />
                        </FloatMenuPanel>
                    </DelayedUnmount>
                </FloatMenuPanel>
            </Layout>
        </div>
    );
};
